import { useState } from 'react';
import { Modal } from 'components/ui/Modal';
import { Question } from 'api/questions/types';
import { SectionBlock } from 'utils/examScenario';
import { getScenarioTitleFromQuestion } from 'utils/examScenario';

const QUESTION_SNIPPET_MAX = 60;

function truncate(s: string | undefined, max: number): string {
  if (!s || typeof s !== 'string') return '';
  return s.length <= max ? s : `${s.slice(0, max).trim()}...`;
}

interface FormSection {
  id: number;
  name: string;
  isActive: boolean;
  questions: Question[];
}

interface Props {
  sections: FormSection[];
  currentSectionId: number;
  block: SectionBlock;
  onMove: (targetSectionIndex: number) => void;
  onClose: () => void;
}

export const MoveScenarioBlockModal = ({
  sections,
  currentSectionId,
  block,
  onMove,
  onClose
}: Props) => {
  const [selectedSectionIndex, setSelectedSectionIndex] = useState<number>(() => {
    const idx = sections.findIndex((s) => s.id === currentSectionId);
    return idx >= 0 ? idx : 0;
  });

  const scenarioTitle = getScenarioTitleFromQuestion(block.questions[0]);
  const targetSection = sections[selectedSectionIndex];
  const isCurrentSection = targetSection?.id === currentSectionId;

  const handleConfirm = () => {
    if (isCurrentSection) return;
    onMove(selectedSectionIndex);
    onClose();
  };

  return (
    <Modal
      title="Move scenario block to section"
      onClose={onClose}
      boxClassName="w-[480px]"
    >
      <div className="space-y-4">
        <p className="text-sm text-black">
          Choose a section to move this scenario block to. All questions in this block will be moved together.
        </p>
        <div>
          <p className="text-sm font-medium text-black mb-1">Scenario: {scenarioTitle || 'Scenario'}</p>
          <p className="text-xs text-black/70">
            Questions in this block: {block.questions.length}
          </p>
        </div>
        <div>
          <label className="fieldLabel block mb-1">Section</label>
          <select
            className="w-full border border-secondary rounded-lg px-3 py-2 text-sm text-black"
            value={selectedSectionIndex}
            onChange={(e) => setSelectedSectionIndex(Number(e.target.value))}
          >
            {sections.map((s, i) => (
              <option key={s.id} value={i}>
                {s.name || 'Unnamed section'}
                {s.id === currentSectionId ? ' (current)' : ''}
              </option>
            ))}
          </select>
        </div>
        {targetSection && (
          <div>
            <p className="text-sm font-medium text-black mb-1">Section details:</p>
            <ul className="list-disc list-inside space-y-0.5 text-sm text-black/90 border border-tertiary rounded-lg p-3 max-h-32 overflow-y-auto">
              {targetSection.questions.length === 0 ? (
                <li className="text-black/70">No questions yet</li>
              ) : (
                targetSection.questions.map((q) => (
                  <li key={q._id}>{truncate(q.question, QUESTION_SNIPPET_MAX)}</li>
                ))
              )}
            </ul>
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <button type="button" className="secondaryBtn flex-1" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="secondaryBtn flex-1"
            onClick={handleConfirm}
            disabled={isCurrentSection}
          >
            Move here
          </button>
        </div>
      </div>
    </Modal>
  );
};
